import { useState, useEffect, useRef } from 'react';

import ReactPlayer from 'react-player';

import { api, useAPIContext } from '../../../api/context';
import { useFPContext } from '../../../utils/fingerprint';
import dialogs from '../../../dialogs';
import toast from '../../../utils/toast';
import Base from '../../Base';
import styles from './MediaUpload.module.scss';

const extractTikTokVideoId = (url) => {
  const match = url.match(/video\/(\d+)/);
  return match ? match[1] : null;
};

const isVideoUrl = (url) => {
  if (!url) return false;
  const path = url.split('?')[0].toLowerCase();
  return /\.(mp4|mov|webm|m4v|avi|mkv|ogv)$/.test(path);
};

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const absoluteMediaUrl = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_URL}${url}`;
};

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm', '.m4v', '.avi', '.mkv', '.ogv', '.3gp'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif'];

const ACCEPT_MAP = {
  'image': 'image/*,.heic,.heif',
  'video': 'video/*,.mov,.m4v,.3gp',
  'both': 'image/*,video/*,.heic,.heif,.mov,.m4v,.3gp',
};

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;        // 10 MB
const MAX_VIDEO_SIZE = 500 * 1024 * 1024;       // 500 MB

const inferMediaKind = (file) => {
  if (!file) return null;
  if (file.type && file.type.startsWith('image/')) return 'image';
  if (file.type && file.type.startsWith('video/')) return 'video';
  const name = (file.name || '').toLowerCase();
  if (VIDEO_EXTENSIONS.some(ext => name.endsWith(ext))) return 'video';
  if (IMAGE_EXTENSIONS.some(ext => name.endsWith(ext))) return 'image';
  return null;
};

const MediaUpload = ({ campaign, activity, handle }) => {

  const { token } = useAPIContext();

  const { setDialog } = dialogs.utils.useDialog();

  /*
   * data
   */

  const [ dataLoading, setDataLoading ] = useState(true);
  const [ data, setData ] = useState();

  const [ hashtags, setHashtags ] = useState('');

  useEffect(() => {

    api(token).v1.campaigns.activities
      .obtain({
        campaign: campaign.id,
        activity: activity.id,
      })
      .then(res => {
        const data = res.body.data;

        setDataLoading(false);
        setData(data);

        setHashtags(
          (data.config.hashtags || [])
          .join(' ')
        );
      })
      .catch(err => {
        setDataLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });

  }, [token, campaign.id, activity.id]);

  /*
   * file
   */

  const fileInputRef = useRef(null);
  const [ selectedFile, setSelectedFile ] = useState(null);
  const [ filePreview, setFilePreview ] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    if (file) {
      setSelectedFile(file);
      if (inferMediaKind(file) === 'image') {
        setFilePreview(URL.createObjectURL(file));
      } else {
        setFilePreview(null);
      }
    }
  };

  /*
   * submit
   */

  const { fp } = useFPContext();

  const [ submitLoading, setSubmitLoading ] = useState(false);
  const [ submitError, setSubmitError ] = useState('');
  const [ customFieldValue, setCustomFieldValue ] = useState('');

  const submit = () => {

    if (!selectedFile) {
      setSubmitError('Please select a file.');
      return;
    }

    const kind = inferMediaKind(selectedFile);
    if (kind === 'image' && selectedFile.size > MAX_IMAGE_SIZE) {
      setSubmitError('Image file is too large. Maximum size is 10 MB.');
      return;
    }
    if (kind !== 'image' && selectedFile.size > MAX_VIDEO_SIZE) {
      setSubmitError('Video file is too large. Maximum size is 500 MB.');
      return;
    }

    if (data?.config?.custom_field_enabled && data?.config?.custom_field_required && !customFieldValue.trim()) {
      setSubmitError(`${data.config.custom_field_label} is required.`);
      return;
    }

    setSubmitError('');
    setSubmitLoading(true);

    fp.get().then(result => {

      const form = new FormData();
      form.append('data_json', JSON.stringify({
        ...(data?.config?.custom_field_enabled && { custom_field: customFieldValue }),
      }));
      form.append('media', selectedFile);
      form.append('fingerprint', result.visitorId);

      api(token).v1.campaigns.activities.submit
        .post(
          {
            campaign: campaign.id,
            activity: activity.id,
          },
          form,
        )
        .then(res => {
          setSubmitLoading(false);

          if (res.status === 200) {
            handle(res.body);
            setDialog();
          }

          if (res.status === 400) {
            toast.show(toast.KIND.ERROR, res.body);
          }
        })
        .catch(err => {
          setSubmitLoading(false);
          toast.show(toast.KIND.WARNING, err);
        });
    });
  };

  /*
   * content
   */

  const mediaAccept = ACCEPT_MAP[data?.config?.media_accept] || ACCEPT_MAP['both'];

  return (
    <Base title='Submit'>
      <div className={styles.root}>
        {
          dataLoading &&
          <div className={styles.loading}>
            Loading...
          </div>
        }

        {
          data && (
            <>
              <div
                className={styles.instr}
                dangerouslySetInnerHTML={{
                  __html: data.instr
                }}
              />

              {hashtags && (
                <div className={styles.field}>
                  <label htmlFor='hashtags'>
                    Use these hashtags in your caption
                  </label>

                  <input
                    className={styles.copy}
                    id='hashtags'
                    type='text'
                    disabled={true}
                    aria-label='hashtags'
                    value={hashtags}
                  />

                  <button
                    type='button'
                    onClick={() => {
                      navigator.clipboard.writeText(hashtags)
                        .then(() => {
                          toast.show(toast.KIND.SUCCESS, 'Hashtags copied.');
                        })
                        .catch(() => {
                          toast.show(toast.KIND.WARNING, 'Could not copy to clipboard. Please copy manually.');
                        });
                    }}>

                    Copy Hashtags

                  </button>
                </div>
              )}

              {data?.config?.custom_field_enabled && (
                <div className={styles.field}>
                  <label htmlFor='custom_field'>
                    {data.config.custom_field_label}
                  </label>

                  <input
                    id='custom_field'
                    aria-label='custom field'
                    type='text'
                    disabled={submitLoading}
                    value={customFieldValue}
                    onChange={(event) => setCustomFieldValue(event.target.value)}
                  />
                </div>
              )}

              {data.result?.media && !selectedFile && (
                <div className={styles.previousSubmission}>
                  <label>Your submission</label>
                  <div className={styles.player}>
                    {isVideoUrl(data.result.media)
                      ? (
                        <ReactPlayer
                          src={absoluteMediaUrl(data.result.media)}
                          controls={true}
                          width='100%'
                          height='100%'
                          playsInline={true}
                        />
                      )
                      : (
                        <img
                          src={absoluteMediaUrl(data.result.media)}
                          alt='Submitted media'
                        />
                      )
                    }
                  </div>
                </div>
              )}

              <div className={styles.field}>
                <label>
                  Upload your file
                </label>

                <input
                  ref={fileInputRef}
                  type='file'
                  accept={mediaAccept}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />

                {filePreview && (
                  <img
                    className={styles.preview}
                    src={filePreview}
                    alt='Preview'
                  />
                )}

                {selectedFile && !filePreview && (
                  <div className={styles.fileName}>
                    {selectedFile.name}
                  </div>
                )}

                <button
                  type='button'
                  disabled={submitLoading}
                  onClick={() => fileInputRef.current?.click()}>
                  {selectedFile ? 'Change File' : 'Choose File'}
                </button>

                <button
                  type='button'
                  disabled={submitLoading || !selectedFile}
                  onClick={() => submit()}>

                  {submitLoading
                    ? <span className={styles.spinner} aria-label='submitting' />
                    : 'Submit'
                  }

                </button>

                {
                  submitError && (
                    <div className={styles.error}>
                      {submitError}
                    </div>
                  )
                }
              </div>

              {data.config.sample_videos?.length > 0 && (
                <div className={styles.videos}>
                  <h3>Check out these videos for inspiration!</h3>
                  {data.config.sample_videos.map((url, index) => {
                    const videoId = extractTikTokVideoId(url);
                    if (!videoId) return null;
                    return (
                      <div key={videoId}>
                        <iframe
                          src={`https://www.tiktok.com/embed/v2/${videoId}`}
                          loading={index > 0 ? 'lazy' : undefined}
                          allowFullScreen
                          allow='encrypted-media'
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )
        }
      </div>
    </Base>
  );
};

export default MediaUpload;
